import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import ProjectLayout from '../../../ProjectLayout';
import SurveyHeader from '../../SurveyHeader';
import { css } from '@emotion/core';
import { onMobileDevice, HIDE_ON_MOBILE_MED } from '../../../../globalStyle/mediaQueries';
import { useMsgText } from '../../../../localization/hooks';
import { Project, Requirement } from '../../../models';
import { Str } from '../../../../localization/translationKeys';
import LocalDiv from '../../../../localization/LocalDiv';
import { FLEX_ROW, FLEX_COLUMN } from '../../../../layout/flexBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReply } from '@fortawesome/free-solid-svg-icons';
import Msg from '../../../../localization/Msg';
import { useAppState } from '../../../../core/redux/hooks';
import LocalBtn from '../../../../localization/LocalBtn';
import { btnStyle } from '../../../../forms/styles/button';
import { COLORS } from '../../../../globalStyle/colors';
import RaterSearchTable from './RaterSearchTable';
import { AddedDirectoryRater } from '../models';
import Spacer from '../../../../layout/Spacer';
import { useDispatch } from 'react-redux';
import { directoryRatersAction } from './directoryRatersAction';
import { addRaterByIdAction } from '../addRaterByIdAction';
import { useHistory, Link } from 'react-router-dom';
import { useCounter } from '../../../../core/hooks/useCounter';
import { useLoading, useError } from '../../../../core/api/hooks';
import { ActionType } from '../../../../core/redux/actionType';
import { Loader } from '../../../../loading/Loader';
import { useTimedToggle } from '../../../../core/hooks/useTimedToggle';
import { useStickyDiff } from '../../../../core/hooks/useStickyDiff';
import { toMap } from '../../../../language/arrays';
import { canAddRaterForLevel } from '../../../module';
import { useTimedState } from '../../../../core/hooks/useTimedState';
import { useRoutes } from '../../../../navigation/routes';
import { withValidMinOrMax, withValidMinimum } from '../module';


const styles = {
  header:       css`
    margin-bottom: 40px;

    ${onMobileDevice} {
      margin-bottom: 35px;
    }
  `,
  subtitleRow:  {
    root:     css`
      justify-content: space-between;
      padding: 16px 8px;
    `,
    subtitle: css`
      font-size: 26px;
    `,
    icon:     css`
      color: ${COLORS.BLUE};
    `,
  },
  bottomBtnRow: {
    root:   css`
      justify-content: flex-end;
      padding: 16px 8px;
    `,
    button: css`
      min-width: 144px;
      height: 35px;
    `,
  },
  loader:       {
    root:    css`
      flex-basis: 480px;
      justify-content: center;
      align-items: center;
      ${onMobileDevice} {
        margin-top: 120px;
      }
    `,
    spinner: css`
      width: 60px;
    `,
  },
};


type Props = {
  className?: string;
  project?: Project;
};

/**
 * RaterDirectorySearch: a component that
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterDirectorySearch: React.FC<Props> = props => {
  const dispatch = useDispatch();
  const history  = useHistory();

  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [addedRater, setAddedRater] = useState<AddedDirectoryRater | undefined>(undefined);
  const [lastAddedRater, setLastAddedRater] = useState<AddedDirectoryRater | undefined>(undefined);

  const [showSuccessMsg, toggleSuccessMsg]         = useTimedToggle(false, 3000);
  const [showNoResultsMsg, toggleNoResultsMsg]     = useTimedToggle();
  const [showAddErrorMsg, toggleAddErrorMsg]       = useTimedToggle(false, 3000);
  const [showSearchErrorMsg, toggleSearchErrorMsg] = useTimedToggle();
  const [maxedOutReq, setMaxedOutReqMsg]           = useTimedState<Requirement | undefined>(undefined);

  const [lastLoadedResultsPage, loadMoreResults] = useCounter(1);

  const pageTitle     = useMsgText(Str.InviteRemindRaters);
  const textDirection = useAppState(state => state.locale.textDirection);
  const project       = useAppState(state => state.projectSummary);
  const participantId = useAppState(state => state.auth.currentUser?.personId);
  const raterLevels   = useAppState(state => state.projectRaterLevels?.[`${project?.projectId}`] || []);
  const lang          = useAppState(state => state.locale.lang);
  const raters        = useAppState(state => state.projectRaters);
  const searchResults = useAppState(
    state => state.directoryRaters
      ?.[`${project?.projectId}`]
      ?.[searchTerm?.toLocaleLowerCase(lang.code) || ''],
  );

  const requirements         = useAppState(state => state.participantRequirements);
  const nonEmptyRequirements = React.useMemo(
    () => !!project?.maximumsRequired ? withValidMinOrMax(requirements) : withValidMinimum(requirements),
    [project, requirements],
  );

  const nonEmptyRequirementMap = React.useMemo(
    () => toMap(
      nonEmptyRequirements ?? [],
      req => `${req.surveyLevelId}`,
    ),
    [nonEmptyRequirements],
  );

  const addedRaterRequirement: Requirement | undefined = nonEmptyRequirementMap[`${addedRater?.levelId}`];

  const searchActionTag    = useMemo(
    () => `${project?.projectId ?? 0}-${participantId}-${searchTerm?.toLocaleLowerCase(lang.code) || ''}`,
    [lang.code, participantId, project, searchTerm],
  );
  const searchingDirectory = useLoading(ActionType.DirectoryRatersRequest, searchActionTag);
  const searchError        = !!useError(ActionType.DirectoryRatersRequest, searchActionTag);

  const addActionTag = useMemo(() => `${addedRater?.personId || 0}-${project?.projectId || 0}`, [addedRater, project]);
  const addingRater  = useLoading(ActionType.AddRaterByIdRequest, addActionTag);
  const addError     = !!useError(ActionType.AddRaterByIdRequest, addActionTag);

  const isLoading = searchingDirectory || addingRater;

  useStickyDiff(
    (prev, next) => !prev?.results && !!next.results && next.results.length === 0 && toggleNoResultsMsg(),
    {
      results: searchResults,
    },
  );

  useStickyDiff(
    (prev, next) => !prev?.error && next.error && toggleSearchErrorMsg(),
    {
      error: searchError,
    },
  );

  useStickyDiff(
    (prev, next) => !prev?.error && next.error && toggleAddErrorMsg(),
    {
      error: addError,
    },
  );

  const toggleSuccess = function() {
    toggleSuccessMsg();
    setLastAddedRater(addedRater);
    setAddedRater(undefined);
    return true;
  };

  useStickyDiff(
    (prev, next) => !!prev?.loading && !next.loading && !next.error && toggleSuccess(),
    {
      loading: addingRater,
      error:   addError,
    },
  );

  useEffect(
    () => {
      (!!searchTerm && !!project)
      && dispatch(directoryRatersAction(project.projectId, participantId || 0, searchTerm));
    },
    [dispatch, participantId, project, searchTerm, raters],
  );

  useStickyDiff(
    (prev, next) => {
      !prev?.hasRater && !!next.hasRater && !!project && !!participantId
      && (
        canAddRaterForLevel(project.maximumsRequired, addedRaterRequirement)
          ? dispatch(addRaterByIdAction(project.projectId, participantId, addedRater!.personId, addedRater!.levelId))
          : setMaxedOutReqMsg(addedRaterRequirement)
      );
    },
    {
      hasRater: addedRater,
    },
  );

  const { InviteRaters } = useRoutes(participantId, project?.displayProjectId);

  return (
    <ProjectLayout className={props.className}>
      <LocalDiv css={styles.header}>
        <SurveyHeader title={`/ ${pageTitle}`} deadline={project?.surveyDeadline} />
      </LocalDiv>
      <LocalDiv css={[FLEX_COLUMN, css`justify-content: stretch;`]}>
        <LocalDiv css={[FLEX_ROW, styles.subtitleRow.root]}>
          <LocalDiv css={[FLEX_ROW, styles.subtitleRow.subtitle]}>
            <Link to={InviteRaters}>
              <FontAwesomeIcon icon={faReply} css={styles.subtitleRow.icon} />
            </Link>
            <Spacer sizePx={8} />
            <Msg str={Str.SearchDirectory} css={styles.subtitleRow.subtitle} />
          </LocalDiv>
          <LocalBtn
            str={Str.Done}
            css={[btnStyle.primary(COLORS.BLUE), styles.bottomBtnRow.button, HIDE_ON_MOBILE_MED]}
            onClick={history.goBack}
          />
        </LocalDiv>
        <RaterSearchTable
          textDirection={textDirection}
          raterLevels={raterLevels}
          searchResults={searchResults ?? []}
          maxedOutRequirement={maxedOutReq}
          setSearchTerm={setSearchTerm}
          lastAddedRater={lastAddedRater}
          addedRater={addedRater}
          setAddedRater={setAddedRater}
          resultsPageSize={20}
          lastLoadedResultsPage={lastLoadedResultsPage}
          loadMoreResults={loadMoreResults}
          showSuccessMsg={showSuccessMsg}
          showNoResultsMsg={showNoResultsMsg}
          showSearchErrorMsg={showSearchErrorMsg}
          showAddErrorMsg={showAddErrorMsg}
        />
        <LocalDiv css={[FLEX_ROW, styles.bottomBtnRow.root]}>
          <LocalBtn
            str={Str.Done}
            css={[btnStyle.primary(COLORS.BLUE), styles.bottomBtnRow.button]}
            onClick={history.goBack}
          />
        </LocalDiv>
      </LocalDiv>
      {
        isLoading && (
          <LocalDiv css={[FLEX_ROW, styles.loader.root]}>
            <Loader css={styles.loader.spinner} />
          </LocalDiv>
        )
      }
    </ProjectLayout>
  );
};

export default RaterDirectorySearch;
